import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Admin (HRM) day-off API — cross-employee view. Backed by:
 *   GET /employees/day_offs/list      — paginated, filterable list (all employees)
 *   GET /employees/day_offs/stats     — aggregate counts/days by status
 *   GET /employees/day_offs/:id       — single record
 * Approve/reject mutations are USER-initiated writes — see DayoffsAdminPage.
 * field spellings verified via probe 2026-08-27.
 */

export interface AdminDayOffParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

/** range_time item — API returns both spellings depending on record age */
export interface DayOffRangeItem {
  start_day_off?: string;
  startdayoff?: string;
  day_off_option?: number | string;
  dayoff_option?: number | string;
}

/** Returns the date string from a range item, tolerating both field spellings. */
export const rangeItemDate = (r: DayOffRangeItem): string =>
  r.start_day_off || r.startdayoff || '';

export interface AdminDayOff {
  day_off_id: number;
  employee_id: number;
  employee_name: string;
  department_name: string;
  position_name?: string;
  day_off_type: string;
  day_off_reason: string;
  day_off_note: string | null;
  day_off_status: 'pending' | 'leader-approved' | 'approved' | 'rejected' | 'cancelled';
  number_of_day_off: number;
  day_off_range_time: DayOffRangeItem[];
  day_off_leader_confirmed: boolean;
  day_off_bod_confirmed: boolean;
  day_off_feedback_lead: string | null;
  day_off_feedback_bod: string | null;
  day_off_created_at: string;
  day_off_updated_at: string;
  day_off_leader_name?: string;
  day_off_leader_confirmed_at?: string | null;
  day_off_bod_name?: string;
  day_off_bod_confirmed_at?: string | null;
}

export interface DayOffStats {
  total_requests: number;
  total_days: number;
  pending_count: number;
  leader_approved_count: number;
  approved_count: number;
  rejected_count: number;
  cancelled_count: number;
  pending_days: number;
  leader_approved_days: number;
  approved_days: number;
  rejected_days: number;
  cancelled_days: number;
}

function toQuery(params: AdminDayOffParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return q.toString();
}

/** Paginated list of ALL employees' day-offs (HRM admin view). */
export function useAdminDayOffs(params: AdminDayOffParams = {}) {
  return useQuery({
    queryKey: ['hrm', 'day-offs', params],
    queryFn: async () => {
      const res = await dataCentralApi.get<ResponseArray<AdminDayOff>>(
        `/employees/day_offs/list?${toQuery({ page: 1, page_size: 20, ...params })}`,
      );
      const data = res.data;
      return { results: data.results ?? [], pagination: data.pagination };
    },
  });
}

/** Aggregate counts + days by status. Optional params mirror the list filters. */
export function useAdminDayOffStats(params: AdminDayOffParams = {}) {
  return useQuery({
    queryKey: ['hrm', 'day-offs', 'stats', params],
    queryFn: async (): Promise<DayOffStats> => {
      const res = await dataCentralApi.get<ResponseObject<DayOffStats>>(
        `/employees/day_offs/stats?${toQuery(params)}`,
      );
      return res.data.result ?? {
        total_requests: 0, total_days: 0,
        pending_count: 0, leader_approved_count: 0, approved_count: 0,
        rejected_count: 0, cancelled_count: 0,
        pending_days: 0, leader_approved_days: 0, approved_days: 0,
        rejected_days: 0, cancelled_days: 0,
      };
    },
  });
}

/** Single day-off record detail. */
export function useAdminDayOffDetail(id: number | undefined) {
  return useQuery({
    queryKey: ['hrm', 'day-offs', 'detail', id],
    queryFn: async (): Promise<AdminDayOff> => {
      const res = await dataCentralApi.get<ResponseObject<AdminDayOff>>(
        `/employees/day_offs/${id}`,
      );
      return res.data.result as AdminDayOff;
    },
    enabled: id !== undefined && id > 0,
  });
}
